// evals/runner.ts — mechanical grader for a single EvalCase.
//
// Composes the existing retrieve() + composer + groundedness pipeline
// (the Path A browser stack) and checks the case's expectations against
// the actual output. No LLM-judge — every check is a string match or
// numeric threshold so the report is reproducible.

import { retrieve } from "@/lib/retrieval";
import { compose } from "@/lib/composer";
import { retrievalGate, checkGroundedness } from "@/lib/groundedness";
import type { EvalCase } from "./cases";

export type CaseResult = {
  id: string;
  kind: EvalCase["kind"];
  query: string;
  rationale: string;
  // pass=case behaved as expected. fail=did not. error=runner crashed.
  status: "pass" | "fail" | "error";
  // Sub-checks — each tells you exactly WHY a case passed or failed.
  checks: {
    mustContain: { substr: string; ok: boolean }[];
    mustNotContain: { substr: string; ok: boolean }[];
    mustRefuse: { expected: boolean; gatePassed: boolean; ok: boolean } | null;
    shouldRetrieve: { ids: string[]; hits: string[]; ok: boolean } | null;
    groundedness: { overall: string; ok: boolean } | null;
  };
  // Aggregate "what went wrong" — empty if status="pass".
  failureReasons: string[];
  // Trace data so the UI can show what happened.
  retrievedIds: string[];
  topBm25: number;
  topEmbedding: number;
  responseText: string | null;
  latencyMs: number;
  errorMessage?: string;
};

const norm = (s: string) => s.toLowerCase();

function checkSubstrings(
  haystack: string,
  expected: string[] | undefined,
  shouldContain: boolean,
): { substr: string; ok: boolean }[] {
  if (!expected || expected.length === 0) return [];
  const lower = norm(haystack);
  return expected.map((s) => {
    const has = lower.includes(norm(s));
    return { substr: s, ok: shouldContain ? has : !has };
  });
}

export async function runEvalCase(c: EvalCase): Promise<CaseResult> {
  const t0 = performance.now();
  const base: CaseResult = {
    id: c.id,
    kind: c.kind,
    query: c.query,
    rationale: c.rationale,
    status: "fail",
    checks: {
      mustContain: [],
      mustNotContain: [],
      mustRefuse: null,
      shouldRetrieve: null,
      groundedness: null,
    },
    failureReasons: [],
    retrievedIds: [],
    topBm25: 0,
    topEmbedding: 0,
    responseText: null,
    latencyMs: 0,
  };

  try {
    const result = await retrieve(c.query, 5);
    base.retrievedIds = result.chunks.map((ch) => ch.id);
    base.topBm25 = result.trace.bm25Hits[0]?.score ?? 0;
    base.topEmbedding = result.trace.semanticHits[0]?.score ?? 0;
    const gate = retrievalGate(base.topBm25, base.topEmbedding);

    // ── Refusal cases: gate must reject. We never run the composer here
    //    because in production we wouldn't either — refusal happens above
    //    the composer in the pipeline.
    if (c.expects.mustRefuse) {
      const ok = !gate.passed;
      base.checks.mustRefuse = {
        expected: true,
        gatePassed: gate.passed,
        ok,
      };
      // If the gate over-confidently passed, also run composer + record
      // its text so we can show *what* it would have said.
      if (gate.passed) {
        const composed = compose(c.query, result.chunks, result.trace.finalHits[0]?.rrfScore ?? 0);
        base.responseText = composed.text;
        const denyHits = checkSubstrings(composed.text, c.expects.mustNotContain, false);
        base.checks.mustNotContain = denyHits;
        base.failureReasons.push("retrieval gate over-confidently passed an off-corpus query");
        if (denyHits.some((h) => !h.ok)) {
          base.failureReasons.push(
            `denylist substrings appeared: ${denyHits
              .filter((h) => !h.ok)
              .map((h) => `"${h.substr}"`)
              .join(", ")}`,
          );
        }
      }
      base.status = ok ? "pass" : "fail";
      base.latencyMs = Math.round(performance.now() - t0);
      return base;
    }

    // ── Non-refusal cases: gate should pass, composer should run.
    if (!gate.passed) {
      base.failureReasons.push("retrieval gate refused an in-corpus query");
      base.status = "fail";
      base.latencyMs = Math.round(performance.now() - t0);
      return base;
    }

    const composed = compose(c.query, result.chunks, result.trace.finalHits[0]?.rrfScore ?? 0);
    base.responseText = composed.text;

    // mustContain
    base.checks.mustContain = checkSubstrings(composed.text, c.expects.mustContain, true);
    // mustNotContain
    base.checks.mustNotContain = checkSubstrings(composed.text, c.expects.mustNotContain, false);
    // shouldRetrieve — at least one of the listed ids must be in retrievedIds
    if (c.expects.shouldRetrieve && c.expects.shouldRetrieve.length > 0) {
      const want = new Set(c.expects.shouldRetrieve);
      const hits = base.retrievedIds.filter((id) => want.has(id));
      base.checks.shouldRetrieve = {
        ids: c.expects.shouldRetrieve,
        hits,
        ok: hits.length > 0,
      };
    }

    // Groundedness check (post-compose) — gives us the trust score we
    // surface in the UI.
    try {
      const report = await checkGroundedness({
        response: composed.text,
        chunks: result.chunks,
        chunkEmbeddings: result.chunkEmbeddings,
        topBM25Score: base.topBm25,
        topEmbeddingCosine: base.topEmbedding,
      });
      base.checks.groundedness = {
        overall: report.overall,
        ok: report.overall === "passed",
      };
    } catch {
      base.checks.groundedness = { overall: "error", ok: true };
    }

    // Aggregate
    const failedMustContain = base.checks.mustContain.filter((c) => !c.ok);
    const failedMustNot = base.checks.mustNotContain.filter((c) => !c.ok);
    const failedRetrieve = base.checks.shouldRetrieve && !base.checks.shouldRetrieve.ok;
    if (failedMustContain.length > 0) {
      base.failureReasons.push(
        `missing required substring(s): ${failedMustContain.map((c) => `"${c.substr}"`).join(", ")}`,
      );
    }
    if (failedMustNot.length > 0) {
      base.failureReasons.push(
        `forbidden substring(s) appeared: ${failedMustNot.map((c) => `"${c.substr}"`).join(", ")}`,
      );
    }
    if (failedRetrieve) {
      base.failureReasons.push(
        `retrieval missed expected chunks (wanted any of ${base.checks.shouldRetrieve!.ids.join(
          ", ",
        )}, got ${base.retrievedIds.join(", ")})`,
      );
    }

    base.status = base.failureReasons.length === 0 ? "pass" : "fail";
    base.latencyMs = Math.round(performance.now() - t0);
    return base;
  } catch (e) {
    base.status = "error";
    base.errorMessage = e instanceof Error ? e.message : String(e);
    base.latencyMs = Math.round(performance.now() - t0);
    return base;
  }
}

export type SuiteResult = {
  ranAt: string; // ISO timestamp
  total: number;
  passed: number;
  failed: number;
  errored: number;
  p50LatencyMs: number;
  p95LatencyMs: number;
  byKind: Record<EvalCase["kind"], { total: number; passed: number }>;
  // When false, the WebGPU MiniLM embedder didn't run — the retrieval
  // gate is BM25-only, which is too permissive for refusal cases.
  // Surfaced in the UI as a note so the numbers stay honest.
  embedderRan: boolean;
  cases: CaseResult[];
};

export async function runSuite(cases: EvalCase[], nowIso: string): Promise<SuiteResult> {
  const results: CaseResult[] = [];
  for (const c of cases) {
    results.push(await runEvalCase(c));
  }
  const lats = results.map((r) => r.latencyMs).sort((a, b) => a - b);
  const pct = (p: number) => lats[Math.max(0, Math.floor((lats.length - 1) * p))] ?? 0;
  const byKind = {
    golden: { total: 0, passed: 0 },
    refusal: { total: 0, passed: 0 },
    adversarial: { total: 0, passed: 0 },
  };
  for (const r of results) {
    byKind[r.kind].total += 1;
    if (r.status === "pass") byKind[r.kind].passed += 1;
  }
  // Any case that produced a non-zero top embedding score means the
  // embedder ran. If none did, we were in node / no-WebGPU mode.
  const embedderRan = results.some((r) => r.topEmbedding > 0);
  return {
    ranAt: nowIso,
    total: results.length,
    passed: results.filter((r) => r.status === "pass").length,
    failed: results.filter((r) => r.status === "fail").length,
    errored: results.filter((r) => r.status === "error").length,
    p50LatencyMs: pct(0.5),
    p95LatencyMs: pct(0.95),
    byKind,
    embedderRan,
    cases: results,
  };
}
